import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useAuthStore } from './auth'
import type { Team, TeamFormData } from '@/types'

function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 60) +
    '-' +
    Math.random().toString(36).slice(2, 8)
}

export const useTeamStore = defineStore('team', () => {
  const teams = ref<Team[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)
  const _loaded = ref(false)

  const getById = (id: string) => teams.value.find(t => t.id === id)

  const fetchTeams = async (force = false, { silent = false } = {}) => {
    const authStore = useAuthStore()
    if (!authStore.userId) return
    if (_loaded.value && !force) return

    if (!silent) {
      loading.value = true
      error.value = null
    }

    try {
      const { supabase } = await import('@/services/supabase')

      // Fetch teams where user is a member (include role for sidebar)
      const { data: memberRows, error: memberErr } = await supabase
        .from('team_members')
        .select('team_id, role')
        .eq('user_id', authStore.userId)

      if (memberErr) throw memberErr
      if (!memberRows || memberRows.length === 0) {
        teams.value = []
        _loaded.value = true
        return
      }

      const roleMap = new Map<string, string>()
      for (const row of memberRows) {
        roleMap.set(row.team_id, row.role)
      }

      const teamIds = memberRows.map(r => r.team_id)
      const { data, error: fetchError } = await supabase
        .from('teams')
        .select('*')
        .in('id', teamIds)
        .order('created_at', { ascending: false })

      if (fetchError) throw fetchError

      // Fetch member counts for each team
      const { data: countRows } = await supabase
        .from('team_members')
        .select('team_id')
        .in('team_id', teamIds)

      const countMap = new Map<string, number>()
      if (countRows) {
        for (const row of countRows) {
          countMap.set(row.team_id, (countMap.get(row.team_id) || 0) + 1)
        }
      }

      teams.value = (data || []).map(t => ({
        ...t,
        member_count: countMap.get(t.id) || 0,
        user_role: roleMap.get(t.id) as Team['user_role'],
      })) as Team[]

      _loaded.value = true
    } catch (err) {
      if (!silent) error.value = err instanceof Error ? err.message : 'Failed to fetch teams'
    } finally {
      if (!silent) loading.value = false
    }
  }

  const createTeam = async (data: TeamFormData) => {
    const authStore = useAuthStore()
    if (!authStore.userId) return { success: false, error: 'Not authenticated' }

    // Tier gate: Teams require a paid tier (Maker). 'commercial'/'farm' are legacy paid tiers.
    const tier = authStore.subscriptionTier
    if (!['maker', 'commercial', 'farm'].includes(tier)) {
      return { success: false, error: 'Teams are available on the Maker plan. Upgrade to create teams.' }
    }

    loading.value = true
    try {
      const { supabase } = await import('@/services/supabase')
      const slug = generateSlug(data.name)

      // Create team
      const { data: team, error: insertError } = await supabase
        .from('teams')
        .insert({
          name: data.name,
          slug,
          owner_id: authStore.userId,
          description: data.description || null,
        })
        .select()
        .single()

      if (insertError) throw insertError

      // Add owner as team member
      const { error: memberError } = await supabase
        .from('team_members')
        .insert({
          team_id: team.id,
          user_id: authStore.userId,
          role: 'owner',
        })

      if (memberError) {
        // Rollback team creation
        await supabase.from('teams').delete().eq('id', team.id)
        throw memberError
      }

      const newTeam: Team = { ...team, member_count: 1 }
      teams.value.unshift(newTeam)
      return { success: true, data: newTeam }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to create team'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  const updateTeam = async (id: string, updates: Partial<TeamFormData>) => {
    loading.value = true
    try {
      const { supabase } = await import('@/services/supabase')
      const { data, error: updateError } = await supabase
        .from('teams')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single()

      if (updateError) throw updateError
      const index = teams.value.findIndex(t => t.id === id)
      if (index !== -1) {
        teams.value[index] = { ...teams.value[index], ...data }
      }
      return { success: true, data }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to update team'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  const deleteTeam = async (id: string) => {
    loading.value = true
    try {
      const { supabase } = await import('@/services/supabase')
      const { error: deleteError } = await supabase.from('teams').delete().eq('id', id)
      if (deleteError) throw deleteError
      teams.value = teams.value.filter(t => t.id !== id)
      return { success: true }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to delete team'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  const clearData = () => {
    teams.value = []
    _loaded.value = false
  }

  return {
    teams, loading, error, _loaded,
    getById,
    fetchTeams, createTeam, updateTeam, deleteTeam,
    clearData,
  }
})
